import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
} from "@mui/material";
import { StripedDataGrid } from "components/StripedDataGrid";
import { GridColDef } from "@mui/x-data-grid";
import BackdropLoading from "components/BackdropLoading";
import { useNavigation } from "contexts/notificationsContext";
import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";

interface ModalAdicionarN1Props {
  open: boolean;
  onClose: () => void;
  inquilinoId: string;
  inquilinoNome: string;
  onSuccess: () => void;
}

interface IMaterialDisponivel {
  id: string;
  tipoMaterial: string;
  referencia: string;
  linha: string;
  data: string;
}

/**
 * Modal para adicionar liberações de Cadastro N1 (Referências + Linhas) para CEM MOBI
 * 
 * IMPORTANTE: CEM MOBI só trabalha com conteúdos "Disponível Mercado"
 * Não há abas de Validação ou Homologação
 */
export const ModalAdicionarN1: React.FC<ModalAdicionarN1Props> = ({
  open,
  onClose,
  inquilinoId,
  inquilinoNome,
  onSuccess,
}) => {
  const { openNotification } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [materiaisDisponiveis, setMateriaisDisponiveis] = useState<IMaterialDisponivel[]>([]);
  const [selecionados, setSelecionados] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      carregarMateriaisDisponiveis();
    }
  }, [open]);

  /**
   * Carrega materiais disponíveis (apenas "Disponível Mercado")
   */
  const carregarMateriaisDisponiveis = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get(`/LiberacaoMOBI/materiaisDisponiveisN1/${inquilinoId}`);
      setMateriaisDisponiveis(data || []);
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Salva liberações selecionadas
   */
  const handleSalvar = async () => {
    if (selecionados.length === 0) {
      openNotification({
        message: "Selecione pelo menos um material para liberar",
        type: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      await api.post("/LiberacaoMOBI/cadastroN1", {
        inquilinoId,
        materiaisIds: selecionados,
      });

      openNotification({
        message: `${selecionados.length} material(is) liberado(s) com sucesso!`,
        type: "success",
      });
      
      onSuccess();
      handleClose();
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelecionados([]);
    onClose();
  };

  const handleSelectionChange = (selectionModel: any[]) => {
    setSelecionados(selectionModel.map(String));
  };

  const columns: GridColDef[] = [
    {
      field: "tipoMaterial",
      headerName: "Tipo material",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "referencia",
      headerName: "Referência",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "linha",
      headerName: "Linha",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "data",
      headerName: "Data",
      flex: 1,
      minWidth: 120,
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={isLoading}
      >
        <DialogTitle>
          Adicionar materiais para a CEM MOBI - {inquilinoNome}
        </DialogTitle>
        
        <DialogContent>
          {/* Aba MERCADO (única para MOBI) */}
          <Box sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#3C90AD" }}>
              MERCADO
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Apresenta quais são as Referências + Linhas com os cadastros de nível 1 na situação "Disponível Mercado".
            </Typography>
          </Box>

          {/* Descrição */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Os cadastros de nível 1 das Referências + Linha selecionadas são os que ficarão disponíveis para importação no software do cliente.
          </Typography>

          {/* Tabela de seleção */}
          <Box sx={{ height: 400 }}>
            <StripedDataGrid
              columns={columns}
              rows={materiaisDisponiveis}
              getRowId={(row) => row.id}
              loading={isLoading}
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selecionados}
              onSelectionModelChange={handleSelectionChange}
              autoHeight={false}
            />
          </Box>

          {/* Contador */}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
            {selecionados.length} item(ns) selecionado(s)
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            CANCELAR
          </Button>
          <Button
            variant="contained"
            onClick={handleSalvar}
            disabled={isLoading || selecionados.length === 0}
          >
            SALVAR
          </Button>
        </DialogActions>
      </Dialog>

      <BackdropLoading isOpen={isLoading} />
    </>
  );
};

